import { ApiError, type ActionPriority, type ActionStatus, type DecisionStatus } from './api.js';
import { askProductAdmin } from './brand.js';

export const DECISION_STATUSES: DecisionStatus[] = ['proposed', 'accepted', 'rejected', 'deferred'];
export const ACTION_STATUSES: ActionStatus[] = ['open', 'done', 'cancelled'];

export const DECISION_LABEL: Record<DecisionStatus, string> = {
  proposed: 'Suggested',
  accepted: 'Agreed',
  rejected: 'Declined',
  deferred: 'Later',
};

export const ACTION_LABEL: Record<ActionStatus, string> = {
  open: 'To do',
  done: 'Done',
  cancelled: 'Cancelled',
};

export type SendDestination = 'email' | 'basecamp';

/** Diarization placeholders, not a person's name. */
export function isPlaceholderName(name: string | null | undefined): boolean {
  if (!name) return true;
  const t = name.trim();
  if (!t) return true;
  if (/^speaker[\s._-]*[a-z0-9]{1,3}$/i.test(t)) return true;
  if (/^unknown(\s+speaker)?$/i.test(t)) return true;
  return false;
}

export function visibleName(name: string | null | undefined): string {
  return isPlaceholderName(name) ? '' : (name ?? '').trim();
}

/**
 * Blank draft keeps a stored placeholder (so a text edit does not wipe "Speaker A").
 * Blank draft clears a real name. A typed name replaces either.
 */
export function nameToSave(draft: string, stored: string | null): string | null {
  const typed = draft.trim();
  if (typed) return typed;
  if (isPlaceholderName(stored)) return stored;
  return null;
}

export function priorityLabel(priority: ActionPriority | null): string | null {
  if (priority === 'high') return 'High';
  if (priority === 'low') return 'Low';
  return null;
}

export function formatDue(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return `Due ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

export function usualDestination(autoPush: boolean, basecampReady: boolean): SendDestination {
  return autoPush && basecampReady ? 'basecamp' : 'email';
}

export function usualHint(usual: SendDestination, basecampReady: boolean): string {
  if (usual === 'basecamp') return 'Your usual place is Basecamp. You can also email them.';
  if (basecampReady) return 'Your usual place is email. You can also send them to Basecamp.';
  return 'Your usual place is email.';
}

const FRIENDLY_SEND_CODES = new Set([
  'email_not_configured',
  'basecamp_not_connected',
  'basecamp_destination_required',
  'email_recipient_required',
  'no_open_actions',
]);

export function describeSendError(err: unknown): string {
  if (err instanceof ApiError && err.code && FRIENDLY_SEND_CODES.has(err.code)) {
    return pushFailureText(err.code);
  }
  const raw = err instanceof Error ? err.message : String(err);
  return pushFailureText(raw);
}

export function pushFailureText(code: string): string {
  if (code === 'email_not_configured') return `Email is not set up yet. ${askProductAdmin('to turn on mail.')}`;
  if (code === 'basecamp_not_connected') return 'Basecamp is not connected. Connect it in Integrations.';
  if (code === 'basecamp_destination_required') return 'Add a Basecamp project number in Integrations.';
  if (code === 'email_recipient_required') return 'Add an email address in Integrations.';
  if (code === 'no_open_actions') return 'There are no open items to send.';
  if (/^[a-z0-9_]+$/.test(code)) return 'That send did not go through. Try again in a moment.';
  return code;
}

export function friendlySaveError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (raw === 'invalid_body' || raw.includes('invalid_body')) {
    return 'Use digits for project and list numbers, and a normal email address.';
  }
  return raw;
}

export function digitsOrBlank(value: string): boolean {
  const trimmed = value.trim();
  return trimmed === '' || /^\d{1,20}$/.test(trimmed);
}
